/**
 * LightCarousel
 * A lightweight native-scroll carousel custom element.
 *
 * Usage:
 *   <light-carousel>
 *     <ul id="my-track" class="light-carousel__track">
 *       <li class="light-carousel__slide">...</li>
 *     </ul>
 *   </light-carousel>
 *
 *   Nav buttons anywhere in the DOM, paired via aria-controls:
 *   <button class="light-carousel__btn light-carousel__btn--prev" aria-controls="my-track">...</button>
 *   <button class="light-carousel__btn light-carousel__btn--next" aria-controls="my-track">...</button>
 *
 *   Pagination dots (optional), paired via data-carousel-dots:
 *   <div class="light-carousel__dots" data-carousel-dots="my-track"></div>
 *   Dots are auto-generated — no need to write the dot markup manually.
 */

if (!customElements.get('light-carousel')) {
  class LightCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.light-carousel__track');
      if (!this.track) return;

      if (!this.track.id) {
        this.track.id = `light-carousel-${Math.random().toString(36).slice(2, 7)}`;
      }

      this.trackId = this.track.id;
      this.slides = [...this.track.querySelectorAll('.light-carousel__slide')];

      this._bindNav();
      this._initDots();

      this.track.addEventListener('scroll', () => this.syncNav(), { passive: true });

      this._resizeObserver = new ResizeObserver(() => this.syncNav());
      this._resizeObserver.observe(this.track);

      this.syncNav();
    }

    disconnectedCallback() {
      this._resizeObserver?.disconnect();
      this._observer?.disconnect();
    }

    // ─── Nav ────────────────────────────────────────────────

    _bindNav() {
      this._getBtns('prev').forEach((btn) => btn.addEventListener('click', () => this.nudge(-1)));
      this._getBtns('next').forEach((btn) => btn.addEventListener('click', () => this.nudge(1)));
    }

    nudge(dir) {
      const firstSlide = this.slides[0];
      if (!firstSlide) return;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      this.track.scrollBy({ left: dir * (firstSlide.offsetWidth + gap), behavior: 'smooth' });
    }

    syncNav() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const atStart = scrollLeft <= 0;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
      this._getBtns('prev').forEach((btn) => this._setDisabled(btn, atStart));
      this._getBtns('next').forEach((btn) => this._setDisabled(btn, atEnd));
    }

    _getBtns(dir) {
      return [...document.querySelectorAll(`.light-carousel__btn--${dir}[aria-controls="${this.trackId}"]`)];
    }

    _setDisabled(btn, disabled) {
      btn.disabled = disabled;
      btn.setAttribute('aria-disabled', disabled);
      btn.classList.toggle('disabled', disabled);
    }

    // ─── Dots ────────────────────────────────────────────────

    _initDots() {
      this.dotsContainer = document.querySelector(`[data-carousel-dots="${this.trackId}"]`);
      if (!this.dotsContainer || !this.slides.length) return;

      // Generate dot elements
      this.dots = this.slides.map((_, i) => {
        const dot = document.createElement('button');
        dot.className = 'light-carousel__dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.setAttribute('type', 'button');
        dot.addEventListener('click', () => this._scrollToSlide(i));
        this.dotsContainer.appendChild(dot);
        return dot;
      });

      // IntersectionObserver to track which slide is in view
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const idx = this.slides.indexOf(entry.target);
              if (idx !== -1) this._activateDot(idx);
            }
          });
        },
        {
          root: this.track,
          threshold: 0.6, // slide must be 60% visible to activate
        },
      );

      this.slides.forEach((slide) => this._observer.observe(slide));
      this._activateDot(0);
    }

    _scrollToSlide(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    }

    _activateDot(index) {
      if (!this.dots) return;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }
  }

  customElements.define('light-carousel', LightCarousel);
}
